import { isEqual } from 'lodash';
import { BULK_SELECTION_TYPE } from '@nile/rmm/src/constants';

export const TREE_COMPONENT_TYPES = {
  FILE_EXPLORER: 'FILE_EXPLORER',
  REGISTRY_EDITOR: 'REGISTRY_EDITOR'
};

export const normalizePathCase = (path, newData) => {
  if (path.length === 0) return path;

  const rootSegment = path[0];
  const existingRootNode = newData.find(
    (node) =>
      node.data.toLowerCase() === rootSegment.toLowerCase() &&
      isEqual(node.parent, [])
  );

  if (existingRootNode) {
    // Use the existing node's case for the root segment
    return [existingRootNode.data, ...path.slice(1)];
  }

  return path;
};

// Build node index from path segments
export const buildNodeIndex = (pathSegments, rootSegment) => {
  const baseIndex = [...pathSegments].reverse().join('---');
  return rootSegment
    ? `${baseIndex}---${rootSegment}`.toLowerCase()
    : baseIndex.toLowerCase();
};

// Find existing node by index with case-insensitive matching
export const findNodeByIndex = (targetIndex, nodeData) => {
  return nodeData.find(
    (node) => node.index.toLowerCase() === targetIndex.toLowerCase()
  );
};

// Add child to parent node
export const addChildToParent = (parentNode, childIndex) => {
  if (!parentNode) return;

  if (!parentNode.children) {
    parentNode.children = [];
  }

  if (!parentNode.children.includes(childIndex)) {
    parentNode.children.push(childIndex);
    parentNode.hasChildren = true;
  }
};

// Create a new node based on component type
export const createNode = (
  segment,
  parentPath,
  nodeIndex,
  normalizedKeypath,
  hasNextChild = false,
  componentType
) => {
  let children = [];

  if (hasNextChild) {
    if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
      // Calculate the index for the immediate next child node in the file system hierarchy
      // Example: for path ["/", "Users", "Documents", "Projects"] and parentPath ["/", "Users"]
      // this extracts ["Users", "Documents", "Projects"] and builds index "Projects---Documents---Users---/"
      children = [
        buildNodeIndex(
          normalizedKeypath.slice(1, parentPath.length + 2),
          normalizedKeypath[0]
        )
      ];
    } else if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
      // Calculate the index for the immediate next child node in the registry key hierarchy
      // Example: for path ["HKEY_LOCAL_MACHINE", "SOFTWARE", "Microsoft"] and parentPath ["HKEY_LOCAL_MACHINE"]
      // this extracts ["HKEY_LOCAL_MACHINE", "SOFTWARE", "Microsoft"] and builds index "Microsoft---SOFTWARE---HKEY_LOCAL_MACHINE"
      children = [
        buildNodeIndex(normalizedKeypath.slice(0, parentPath.length + 2))
      ];
    }
  }

  const baseNodeData = {
    index: nodeIndex,
    canMove: true,
    hasChildren: true,
    data: segment,
    parent: [...parentPath],
    children,
    canRename: true
  };

  // Add component-specific properties
  if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
    return {
      ...baseNodeData,
      isDir: true,
      isHidden: 'false',
      noOfChildToShow: 49
    };
  }
  if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
    return {
      ...baseNodeData,
      noOfChildToShow: 100
    };
  }

  return baseNodeData;
};

// Find root parent node with component-specific logic
export const findRootParentNode = (
  rootSegment,
  clonedNewData,
  componentType,
  parentIndex = null
) => {
  // Try to find by data and empty parent first (case-insensitive for root)
  let parentNode = clonedNewData.find(
    (node) =>
      node.data.toLowerCase() === rootSegment.toLowerCase() &&
      isEqual(node.parent, [])
  );

  if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
    // Try alternative parent patterns if not found
    if (!parentNode) {
      parentNode = clonedNewData.find(
        (node) =>
          node.data.toLowerCase() === rootSegment.toLowerCase() &&
          (isEqual(node.parent, []) ||
            isEqual(node.parent, ['/']) ||
            node.parent.includes('/'))
      );
    }

    // Find by index (case-insensitive)
    if (!parentNode && parentIndex) {
      parentNode = clonedNewData.find(
        (node) => node.index.toLowerCase() === parentIndex.toLowerCase()
      );
    }
  } else if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
    // Fallback to root node if not found
    if (!parentNode) {
      parentNode = clonedNewData.find((node) => node.index === 'root');
    }
  }

  return parentNode;
};

// Adding missing nodes and relationships to the new data
// This function ensures all intermediate nodes exist in the tree structure when path search is applied
export const addMissingNodesAndRelationships = (
  currentKeypath,
  clonedNewData,
  componentType
) => {
  // Normalize the currentKeypath to use existing case
  const normalizedKeypath = normalizePathCase(currentKeypath, clonedNewData);

  // Process each segment in the path (starting from index 1)
  for (let i = 1; i < normalizedKeypath.length; i++) {
    const segment = normalizedKeypath[i];
    const parentPath = normalizedKeypath.slice(0, i);
    const isLastSegment = i === currentKeypath.length - 1;
    const isFirstLevel = i === 1;

    let nodeIndex;
    let parentIndex;

    // Build indexes based on component type
    if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
      const rootSegment = normalizedKeypath[0];
      nodeIndex = buildNodeIndex(
        normalizedKeypath.slice(1, i + 1),
        rootSegment
      );
      parentIndex = buildNodeIndex(normalizedKeypath.slice(0, i), rootSegment);
    } else if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
      nodeIndex = buildNodeIndex(normalizedKeypath.slice(0, i + 1));
      parentIndex = buildNodeIndex(normalizedKeypath.slice(0, i));
    }

    // Find or create the current node
    let currentNode = findNodeByIndex(nodeIndex, clonedNewData);

    if (!currentNode) {
      // Create new node if it doesn't exist
      currentNode = createNode(
        segment,
        parentPath,
        nodeIndex,
        normalizedKeypath,
        !isLastSegment,
        componentType
      );
      clonedNewData.push(currentNode);
    } else if (!isLastSegment) {
      // Update existing intermediate node with child information
      let childIndex;
      if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
        const rootSegment = normalizedKeypath[0];
        childIndex = buildNodeIndex(
          normalizedKeypath.slice(1, i + 2),
          rootSegment
        );
      } else if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
        childIndex = buildNodeIndex(normalizedKeypath.slice(0, i + 2));
      }
      addChildToParent(currentNode, childIndex);
    }

    // Establish parent-child relationship in the tree hierarchy
    if (isFirstLevel) {
      // Handle root level parent
      let parentNode;
      if (componentType === TREE_COMPONENT_TYPES.FILE_EXPLORER) {
        const rootSegment = normalizedKeypath[0];
        parentNode = findRootParentNode(
          rootSegment,
          clonedNewData,
          componentType,
          parentIndex
        );
      } else if (componentType === TREE_COMPONENT_TYPES.REGISTRY_EDITOR) {
        parentNode = findRootParentNode(
          normalizedKeypath[0],
          clonedNewData,
          componentType
        );
      }
      addChildToParent(parentNode, nodeIndex);
    } else {
      // Handle deeper level parents
      const parentNode = clonedNewData.find(
        (node) =>
          node.index.toLowerCase() === parentIndex.toLowerCase() &&
          isEqual(node.parent, normalizedKeypath.slice(0, i - 1))
      );
      addChildToParent(parentNode, nodeIndex);
    }
  }
};

/**
 * Builds selection input object for bulk actions
 * Determines selection type and constructs the appropriate input structure
 * @param bulkRecords - The bulk records object containing selection data
 * @param viewId - Optional view ID for asset view context
 * @param viewSubType - View subtype, defaults to 'WORKSTATION_ASSET'
 * @param recordKeyName - The name of the key (primary key) on each record.
 * @returns Selection input object with type, IDs, and optional view/filter data
 */
export const buildSelectionInput = ({
  bulkRecords = {},
  viewId = '',
  viewSubType = 'WORKSTATION_ASSET',
  recordKeyName = 'assetId'
}) => {
  const {
    allSelected,
    selectedRecords = [],
    unSelectedRecords = [],
    filterCondition
  } = bulkRecords;

  let selectionType = allSelected ? BULK_SELECTION_TYPE.ALL : '';
  const recordsToMap = unSelectedRecords?.length
    ? unSelectedRecords
    : selectedRecords;

  if (!allSelected && unSelectedRecords?.length) {
    selectionType = BULK_SELECTION_TYPE.UNSELECTED;
  } else if (!allSelected && selectedRecords?.length) {
    selectionType = BULK_SELECTION_TYPE.SELECTED;
  }

  // Validate and handle different states of filterCondition
  let validatedFilterCondition = null;

  if (filterCondition !== undefined && filterCondition !== null) {
    // Check if it's an object and has properties
    if (
      typeof filterCondition === 'object' &&
      Object.keys(filterCondition).length > 0
    ) {
      validatedFilterCondition = filterCondition;
    }
    // If it's an empty object, null, or other falsy values, keep it as null
  }

  return {
    selectionType,
    filterCondition: validatedFilterCondition,
    ...(!allSelected
      ? { ids: recordsToMap.map((item) => ({ id: item[recordKeyName] })) }
      : {}),
    // Add view information if available
    ...(viewId
      ? {
          view: {
            id: viewId,
            viewSubType
          }
        }
      : { view: null })
  };
};
